import React from 'react';
import {
  AcceptedCurrency,
  ConfigResponse,
  FeeValue,
  PartialPaymentValue,
} from '../../store/modules/merchant/types';
import PageWrapper from '../../common/PageWrapper';
import { useMerchant } from '../../contexts/MerchantContext';
import { FeesContent } from './components/FeesContent';
import { useGetConfigsQuery, useGetCredentialsQuery } from '../../store/modules/merchant/api';
import { toast } from '../../util/toast';
import { useConfigUpsert } from '../../hooks';
import type { ConfigToUpsert } from '../../hooks/useConfigUpsert';
import { CONFIG_SAVE_ERROR_FALLBACK } from '../../util/partialPaymentConfig';
import { getClientFacingApiErrorMessage } from '../../util/helpers';

const handleFeeSaveError = (error: unknown): void => {
  const errorMessage = getClientFacingApiErrorMessage(error);

  if (errorMessage?.match(/Cannot use external surcharge fee.*express/i)) {
    toast.error(
      'An error occurred while saving these changes. Worldpay Fee Assist can only be used when Express is set as current payment gateway. Please update your payment gateway in the Payments & Processing page.',
    );
    return;
  }

  toast.error(errorMessage || CONFIG_SAVE_ERROR_FALLBACK);
};

export const Fees = () => {
  const { merchantId, isLoading: merchantInfoLoading, canUpdateMerchantSettings } = useMerchant();

  const { onConfigUpsertBatch, isLoading: isSaving } = useConfigUpsert(merchantId);
  const { data: configs, isLoading: isLoadingConfigs } = useGetConfigsQuery(merchantId, {
    skip: !merchantId,
  });

  const { data: credentialData, isLoading: isLoadingCredentials } = useGetCredentialsQuery(
    { merchantId, type: ['payment'] },
    { skip: !merchantId },
  );

  const isDefaultCredentialExpress = React.useMemo(() => {
    const defaultPaymentCredentialId = credentialData?.payment?.default;
    const paymentCredentials = credentialData?.payment?.credentials ?? [];

    if (paymentCredentials.length > 0 && defaultPaymentCredentialId) {
      const defaultCredential = paymentCredentials.find(
        (cred) => cred.credentialId === defaultPaymentCredentialId,
      );
      return defaultCredential?.systemOfOrigin?.toLowerCase() === 'express';
    }
    return false;
  }, [credentialData?.payment?.credentials, credentialData?.payment?.default]);

  const generalFeeConfig = configs?.find((c) => c.name === 'GENERAL_FEE') as
    ConfigResponse<FeeValue> | undefined;
  const surchargeFeeConfig = configs?.find((c) => c.name === 'SURCHARGE_FEE') as
    ConfigResponse<FeeValue> | undefined;
  const discountsConfig = configs?.find((c) => c.name === 'DISCOUNTS') as
    ConfigResponse<boolean> | undefined;
  const partialPaymentsConfig = configs?.find((c) => c.name === 'PARTIAL_PAYMENTS') as
    ConfigResponse<PartialPaymentValue> | undefined;

  const acceptedCurrencyConfig = configs?.find((c) => c.name === 'ACCEPTED_CURRENCY') as
    ConfigResponse<AcceptedCurrency> | undefined;

  const hasModifyAccess = canUpdateMerchantSettings();

  return (
    <PageWrapper
      showParentHierarchyBanner
      data-testid="FEES"
      title="Fees & Discounts"
      description="Use this page to customize the fees and discounts you add to your customers' orders."
      isLoading={isLoadingConfigs || merchantInfoLoading || isLoadingCredentials}
    >
      <FeesContent
        key={configs?.[0]?.merchantId ?? merchantId}
        generalConfig={generalFeeConfig}
        surchargeConfig={surchargeFeeConfig}
        discountsConfig={discountsConfig}
        partialPaymentsConfig={partialPaymentsConfig}
        readOnly={!hasModifyAccess}
        isSaving={isSaving}
        acceptedCurrency={acceptedCurrencyConfig}
        isWorldpayFeeAssistToggleEnabled={isDefaultCredentialExpress}
        onSave={async ({
          generalFeeConfig: newGeneralFeeConfig,
          surchargeFeeConfig: newSurchargeFeeConfig,
          discountsConfig: newDiscountsConfig,
        }) => {
          try {
            const configsToSave: ConfigToUpsert[] = [];
            if (newGeneralFeeConfig) {
              configsToSave.push({
                enabled: newGeneralFeeConfig.enabled,
                name: 'GENERAL_FEE',
                value: newGeneralFeeConfig.value,
                configId: generalFeeConfig?.configId,
                merchantId: generalFeeConfig?.merchantId,
              });
            }
            if (newSurchargeFeeConfig) {
              configsToSave.push({
                enabled: newSurchargeFeeConfig.enabled,
                name: 'SURCHARGE_FEE',
                value: newSurchargeFeeConfig.value,
                configId: surchargeFeeConfig?.configId,
                merchantId: surchargeFeeConfig?.merchantId,
              });
            }
            if (newDiscountsConfig !== undefined) {
              configsToSave.push({
                enabled: newDiscountsConfig.enabled,
                name: 'DISCOUNTS',
                value: newDiscountsConfig.enabled,
                configId: discountsConfig?.configId,
                merchantId: discountsConfig?.merchantId,
              });
            }
            if (configsToSave.length === 0) {
              return;
            }
            await onConfigUpsertBatch(configsToSave);
            toast.success('Changes saved successfully');
          } catch (e) {
            handleFeeSaveError(e);
            throw e;
          }
        }}
      />
    </PageWrapper>
  );
};
